import {
    FormControl,
    MenuItem,
    Select, SelectChangeEvent
} from "@mui/material";
import React, {useEffect} from "react";
import {getS3Payload} from "../../service/ApiService";
import {useAuthenticator} from "@aws-amplify/ui-react";
import {PayloadDetail} from "../../types/Result";
import CodeMirror from '@uiw/react-codemirror';
import {json} from '@codemirror/lang-json';
import {xml} from '@codemirror/lang-xml';
import {fetchAuthSession} from "aws-amplify/auth";
import formatXml from "xml-formatter";

type PayloadSelectorProps = {
    payloadList: PayloadDetail[],
    defaultSelectedFile: string,
    correlationId: string,
    target: string,
    label: string
}

function tryParseAsXml(text: string): string {
  if (!text) text = "";
  const trimmed = text.trim();

  // Must start with a tag or XML declaration
  if (!trimmed.startsWith("<")) return trimmed;

  // Quick sanity check for matching angle brackets
  if (!trimmed.endsWith(">")) return trimmed;

  try {
    return formatXml(trimmed, {
        indentation: "  ",
        lineSeparator: "\n",
        collapseContent: true,
    });
  } catch (e) {
    console.info("Content does not seem to be XML, returning content as-is", e);
    return trimmed;
  }
}

export const PayloadSelector = (props: PayloadSelectorProps) => {
    const [selectedFile, setSelectedFile] = React.useState(props.defaultSelectedFile);
    const [selectedFilePayload, setSelectedFilePayload] = React.useState('');
    const {user} = useAuthenticator((context) => [
        context.user
    ]);
    const [isFirstEmptyRedirect, setFirstEmptyRedirect] = React.useState(true);

    const fetchPayload = React.useCallback(async (corrId: string, target: string, payload: string, authToken: string)  => {
        const response = await getS3Payload(corrId, target, payload, authToken);
        if (payload === "raw" && response.status === 204 && isFirstEmptyRedirect) {
            setFirstEmptyRedirect(false);
            setSelectedFile("landing");
        }
        return response.data.result;
    }, [isFirstEmptyRedirect, setFirstEmptyRedirect, setSelectedFile]);

    useEffect(() => {
        async function fetchData() {
            const response = await fetchPayload(props.correlationId, props.target, selectedFile, (await fetchAuthSession()).tokens?.idToken?.toString() || "");
            setSelectedFilePayload(response)
        }

        fetchData().catch((e: any) => {
            console.log(e);
            //Handle for error later.
        })
    }, [props.correlationId, selectedFile, user, props.target, fetchPayload]);

    const onChange = async (event: SelectChangeEvent) => {
        setSelectedFile(event.target.value);

        async function fetchData() {
            const response = await fetchPayload(props.correlationId, props.target, event.target.value, (await fetchAuthSession()).tokens?.idToken?.toString() || "")
            setSelectedFilePayload(response);
        }

        await fetchData()
    }
    const extensions = [json(),xml()]
    return <FormControl fullWidth>
        <FormControl sx={{m: 1}}>
            <Select autoWidth={true} id={`selectpayload-${props.label}`} className={"select_payload"}
                    placeholder={"Choose an available payload."} onChange={onChange}
                    value={props.payloadList.map((payloadDetail) => payloadDetail.id).includes(selectedFile) ? selectedFile : ''}>
                {props.payloadList.map((payload: PayloadDetail) => {
                    return <MenuItem value={payload.id} key={`left-${payload.id}`}>{payload.name}</MenuItem>
                })}
            </Select>
        </FormControl>
        <CodeMirror value={(typeof selectedFilePayload) === "object" ? JSON.stringify(selectedFilePayload, null, 2) : tryParseAsXml(selectedFilePayload)} height="auto" extensions={extensions} 
                    id={`textfield-${props.label}`} readOnly={true} theme={"light"}
                    style={{border: "1px solid #bbb", textAlign: "left"}}/>
    </FormControl>
}